import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const configured = Boolean(url && anonKey);

/** Where the session lives in localStorage; removed by hand when a sign-out can't reach the server. */
export const AUTH_STORAGE_KEY = "kept.auth";

/**
 * The access token was refused (401) while this browser still holds a session: it expired by the server's clock while
 * this computer's clock says it's fresh, or a refresh elsewhere replaced it. One shared refresh for every call that hit
 * it; the new token, or null when the session really ended (the refresh then signs the app out, and the sign-in shows).
 */
let renewing: Promise<string | null> | null = null;
export function renewSession(): Promise<string | null> {
  renewing ??= (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) return null;
      const { data: fresh, error } = await supabase.auth.refreshSession();
      return error ? null : fresh.session?.access_token ?? null;
    } catch {
      return null;
    } finally {
      setTimeout(() => { renewing = null; }, 0);
    }
  })();
  return renewing;
}

/** Reads and writes of the database and storage: a refused session token is renewed once and the request made again. */
async function fetchRenewing(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  const href = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  if (res.status !== 401 || !/\/(rest|storage)\/v1\//.test(href) || typeof input !== "string" && !(input instanceof URL)) return res;
  const headers = new Headers(init?.headers);
  if (!/^Bearer\s/i.test(headers.get("Authorization") ?? "")) return res;
  const fresh = await renewSession();
  if (!fresh) return res;
  headers.set("Authorization", `Bearer ${fresh}`);
  return fetch(input, { ...init, headers });
}

/** The one client for the whole app; the session persists in localStorage and refreshes itself. */
export const supabase = createClient(url ?? "http://localhost", anonKey ?? "missing", {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, storageKey: AUTH_STORAGE_KEY },
  global: { fetch: fetchRenewing },
});

export const SUPABASE_URL = url ?? "";
export const SUPABASE_ANON_KEY = anonKey ?? "";
